// Pure streak presentation helpers — milestones, labels, rank formatting.
// No React, no DOM: shared by the client components and unit-tested directly.

/** The celebration rungs a streak climbs toward. */
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100, 180, 365] as const;

/** The next milestone strictly above `streak`, or null once past the last one. */
export function nextMilestone(streak: number): number | null {
  for (const m of STREAK_MILESTONES) {
    if (m > streak) return m;
  }
  return null;
}

/** The milestone at-or-below `streak` (the rung already reached), or 0. */
export function reachedMilestone(streak: number): number {
  let reached = 0;
  for (const m of STREAK_MILESTONES) {
    if (m <= streak) reached = m;
  }
  return reached;
}

export interface MilestoneProgress {
  /** Target rung, or null when every milestone is cleared. */
  next: number | null;
  /** The rung the current run started from (previous milestone, or 0). */
  from: number;
  /** 0–100 progress from `from` toward `next` (100 when maxed out). */
  pct: number;
  /** Days left to reach `next` (0 when maxed out). */
  remaining: number;
}

export function milestoneProgress(streak: number): MilestoneProgress {
  const next = nextMilestone(streak);
  if (next === null) {
    return { next: null, from: reachedMilestone(streak), pct: 100, remaining: 0 };
  }
  const from = reachedMilestone(streak);
  const span = next - from;
  const done = streak - from;
  const pct = span > 0 ? Math.min(100, Math.max(0, Math.round((done / span) * 100))) : 0;
  return { next, from, pct, remaining: Math.max(0, next - streak) };
}

/** "1 day" / "7 days". */
export function streakLabel(days: number): string {
  return `${days} day${days === 1 ? "" : "s"}`;
}

/** "#42 of 1,280" with thousands separators. */
export function formatRank(rank: number, total: number): string {
  return `#${rank.toLocaleString()} of ${total.toLocaleString()}`;
}

/** "top 3%" style descriptor from a 0–100 percentile, or null when unranked. */
export function topPercentLabel(percentile: number, rank: number): string | null {
  if (rank <= 0) return null;
  if (rank === 1) return "top spot";
  const top = Math.max(1, Math.min(100, 100 - percentile));
  return `top ${top}%`;
}

/**
 * A short, momentum-building line for the streak card, keyed off how close the
 * next milestone is. Deterministic (no randomness) so it never flickers.
 */
export function streakEncouragement(streak: number): string {
  if (streak <= 0) return "Build something today to start your streak.";
  const { next, remaining } = milestoneProgress(streak);
  if (next === null) return "You're a building machine. Keep the flame alive.";
  if (remaining === 1) return `One more day to hit a ${next}-day streak.`;
  if (remaining <= 3) return `${remaining} days to a ${next}-day streak — so close.`;
  if (streak === 1) return "Nice start. Come back tomorrow to keep it going.";
  return `${remaining} days to your next milestone (${next} days).`;
}
